/**
 * The desktop "Add Printer" flow: discover → select → check → test → save.
 *
 * A Star LAN printer has no pairing step (Workstream B, B1): the register
 * finds an address, confirms something that answers like a Star printer is
 * there, and saves that endpoint. This hook is that sequence, with one rule
 * the old one-click flow did not keep — **nothing is saved as the register's
 * printer until the operator has seen it answer, and ideally print**, unless
 * they explicitly choose to save anyway (for example, a printer that is
 * switched off right now).
 *
 * Every step here is printer-only. Nothing reads or writes payment state, so a
 * failed check or test print can never re-trigger a payment.
 */
import { useRef, useState, useCallback } from 'react';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { PrintType } from 'src/services/printing/print-types';
import { renderToBase64 } from 'src/services/printing/renderer-registry';
import {
  starPrintService,
  type StarPrinter,
  type StarPrinterStatus,
  type StarDiscoveryDiagnostics,
} from 'src/services/printers';

import { printerForAddress, checkLanPrinterAddress } from './lan-address';

export type SetupMode = 'discover' | 'manual';

export interface ScanState {
  readonly phase: 'idle' | 'searching' | 'done' | 'failed';
  readonly printers: StarPrinter[];
  readonly message?: string;
  readonly diagnostics?: StarDiscoveryDiagnostics;
  readonly error?: string;
}

export interface Candidate {
  readonly printer: StarPrinter;
  readonly source: 'discovered' | 'manual';
  readonly check: 'checking' | 'reachable' | 'unreachable';
  readonly status?: StarPrinterStatus;
  readonly checkError?: string;
  readonly test: 'idle' | 'printing' | 'printed' | 'failed';
  readonly testError?: string;
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function useLanPrinterSetup(onSaved: (printer: StarPrinter) => void) {
  const business = useDeviceSyncStore((state) => state.businessData);

  const [mode, setMode] = useState<SetupMode>('discover');
  const [scan, setScan] = useState<ScanState>({ phase: 'idle', printers: [] });
  const [addressInput, setAddressInput] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [saving, setSaving] = useState(false);
  /** A candidate connection was opened that the saved default does not own. */
  const openedCandidate = useRef(false);

  const discover = useCallback(async () => {
    setScan((current) => ({ ...current, phase: 'searching', error: undefined }));
    try {
      const result = await starPrintService.discoverPrinters({
        interfaceTypes: ['lan'],
        sweepSubnet: true,
        timeoutMs: 15_000,
      });
      setScan({
        phase: 'done',
        printers: result.printers,
        message: result.message,
        diagnostics: result.diagnostics,
      });
    } catch (error) {
      setScan({ phase: 'failed', printers: [], error: messageOf(error, 'The search failed.') });
    }
  }, []);

  /** Opens a connection to `printer` and records what it said about itself. */
  const check = useCallback(async (printer: StarPrinter, source: Candidate['source']) => {
    setCandidate({ printer, source, check: 'checking', test: 'idle' });
    try {
      openedCandidate.current = true;
      const result = await starPrintService.connectPrinter(printer.connection);
      setCandidate({
        // Main's own description of the endpoint wins over the one we built.
        printer: result.printer ?? printer,
        source,
        check: 'reachable',
        status: result.status,
        test: 'idle',
      });
    } catch (error) {
      setCandidate({
        printer,
        source,
        check: 'unreachable',
        checkError: messageOf(error, 'The printer did not answer.'),
        test: 'idle',
      });
    }
  }, []);

  const selectDiscovered = useCallback(
    (printer: StarPrinter) => check(printer, 'discovered'),
    [check]
  );

  const checkAddress = useCallback(async () => {
    const parsed = checkLanPrinterAddress(addressInput);
    if (!parsed.ok) {
      setAddressError(parsed.message);
      return;
    }
    setAddressError(null);
    await check(printerForAddress(parsed.ipAddress, parsed.port), 'manual');
  }, [addressInput, check]);

  const testPrint = useCallback(async () => {
    if (candidate === null) return;
    setCandidate({ ...candidate, test: 'printing', testError: undefined });
    try {
      const image = await renderToBase64(PrintType.TEST, { business: business ?? undefined });
      const result = await starPrintService.printTestImageTo(candidate.printer.connection, image);
      if (!result.success) {
        throw new Error(result.error?.message ?? result.message ?? 'The test page did not print.');
      }
      setCandidate({ ...candidate, check: 'reachable', test: 'printed' });
    } catch (error) {
      setCandidate({
        ...candidate,
        test: 'failed',
        testError: messageOf(error, 'The test page did not print.'),
      });
    }
  }, [business, candidate]);

  /**
   * Saves the candidate as this register's printer. The "test page first" rule
   * is a UI gate the operator may explicitly override; the destination rule
   * in main is not, and still applies to every later connection.
   */
  const save = useCallback(async () => {
    if (candidate === null) return false;
    setSaving(true);
    try {
      await starPrintService.saveDefaultPrinter(candidate.printer);
      // The trial connection is now the registered printer's connection.
      openedCandidate.current = false;
      setCandidate(null);
      onSaved(candidate.printer);
      return true;
    } catch (error) {
      setCandidate({
        ...candidate,
        testError: messageOf(error, 'The printer could not be saved.'),
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [candidate, onSaved]);

  /**
   * Leaves the check step without saving. The candidate connection is closed so
   * the next receipt reconnects to the saved printer rather than to whatever
   * was being tried out.
   */
  const discardCandidate = useCallback(async () => {
    setCandidate(null);
    if (!openedCandidate.current) return;
    openedCandidate.current = false;
    await starPrintService.disconnectPrinter().catch(() => undefined);
  }, []);

  const changeMode = useCallback((next: SetupMode) => {
    setMode(next);
    setAddressError(null);
  }, []);

  return {
    mode,
    changeMode,
    scan,
    discover,
    addressInput,
    setAddressInput,
    addressError,
    checkAddress,
    candidate,
    selectDiscovered,
    testPrint,
    save,
    saving,
    discardCandidate,
  };
}

export type LanPrinterSetup = ReturnType<typeof useLanPrinterSetup>;
